
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ArrowLeft, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/lib/auth/auth-context"
import { createRecurringTransaction, updateRecurringTransaction } from "@/lib/services/recurring-transactions"
import type { RecurringTransaction } from "@/lib/types"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface AddRecurringTransactionProps {
  onClose: () => void
  onSave: () => void
  editTransaction?: RecurringTransaction | null
}

export function AddRecurringTransaction({ onClose, onSave, editTransaction }: AddRecurringTransactionProps) {
  const { user } = useAuth()
  const [amount, setAmount] = useState(editTransaction?.amount.toString() || "")
  const [description, setDescription] = useState(editTransaction?.name || "")
  const [selectedCategory, setSelectedCategory] = useState(editTransaction?.category || "")
  const [type, setType] = useState<"expense" | "income">(editTransaction?.type || "expense")
  const [frequency, setFrequency] = useState<"weekly" | "monthly">(editTransaction?.frequency || "monthly")
  const [dayOfWeek, setDayOfWeek] = useState<string>(editTransaction?.dayOfWeek?.toString() || "1")
  const [dayOfMonth, setDayOfMonth] = useState<string>(editTransaction?.dayOfMonth?.toString() || "1")
  const [isSaving, setIsSaving] = useState(false)

  const expenseCategories = [
    { name: "Food", icon: "🍔", color: "bg-chart-3/10 text-chart-3" },
    { name: "Transport", icon: "🚗", color: "bg-chart-2/10 text-chart-2" },
    { name: "Shopping", icon: "🛍️", color: "bg-chart-1/10 text-chart-1" },
    { name: "Entertainment", icon: "🎬", color: "bg-chart-5/10 text-chart-5" },
    { name: "Health", icon: "💪", color: "bg-chart-4/10 text-chart-4" },
    { name: "Bills", icon: "📄", color: "bg-primary/10 text-primary" },
    { name: "Other", icon: "📦", color: "bg-muted text-muted-foreground" },
  ]

  const incomeCategories = [
    { name: "Salary", icon: "💼", color: "bg-chart-1/10 text-chart-1" },
    { name: "Freelance", icon: "💻", color: "bg-chart-2/10 text-chart-2" },
    { name: "Business", icon: "🏢", color: "bg-chart-3/10 text-chart-3" },
    { name: "Investment", icon: "📈", color: "bg-chart-4/10 text-chart-4" },
    { name: "Other", icon: "💰", color: "bg-success/10 text-success" },
  ]

  const currentCategories = type === "expense" ? expenseCategories : incomeCategories

  const weekDays = [
    { value: "0", label: "Sunday" },
    { value: "1", label: "Monday" },
    { value: "2", label: "Tuesday" },
    { value: "3", label: "Wednesday" },
    { value: "4", label: "Thursday" },
    { value: "5", label: "Friday" },
    { value: "6", label: "Saturday" },
  ]

  useEffect(() => {
    const categoryExists = currentCategories.some((c) => c.name === selectedCategory)
    if (!categoryExists && selectedCategory) {
      setSelectedCategory("")
    }
  }, [type, currentCategories, selectedCategory])

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount")
      return
    }

    if (!selectedCategory) {
      toast.error("Please select a category")
      return
    }

    if (frequency === "weekly" && !dayOfWeek) {
      toast.error("Please select a day of the week")
      return
    }

    if (frequency === "monthly" && !dayOfMonth) {
      toast.error("Please select a day of the month")
      return
    }

    setIsSaving(true)

    try {
      if (!user) {
        toast.error("You must be logged in to add recurring transactions")
        return
      }

      if (editTransaction) {
        await updateRecurringTransaction(editTransaction.id, {
          name: description || `${selectedCategory} ${type}`,
          amount: parseFloat(amount),
          category: selectedCategory,
          type,
          frequency,
          dayOfWeek: frequency === "weekly" ? parseInt(dayOfWeek) : null,
          dayOfMonth: frequency === "monthly" ? parseInt(dayOfMonth) : null,
        })
        toast.success("Recurring transaction updated successfully!")
      } else {
        await createRecurringTransaction(user.id, {
          name: description || `${selectedCategory} ${type}`,
          amount: parseFloat(amount),
          category: selectedCategory,
          type,
          frequency,
          dayOfWeek: frequency === "weekly" ? parseInt(dayOfWeek) : null,
          dayOfMonth: frequency === "monthly" ? parseInt(dayOfMonth) : null,
        })
        toast.success("Recurring transaction created successfully!")
      }

      onSave()
      setTimeout(() => onClose(), 500)
    } catch (error) {
      console.error('Error saving recurring transaction:', error)
      toast.error('Failed to save recurring transaction. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-background z-[60] flex flex-col animate-in slide-in-from-bottom duration-300">
      <div className="sticky top-0 bg-card border-b shadow-sm z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSaving}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">
            {editTransaction ? "Edit" : "Add"} Recurring Transaction
          </h1>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-sm font-semibold min-w-[80px]"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6 max-w-md mx-auto">
          <div className="flex gap-2 p-1 bg-muted rounded-lg">
            <Button
              variant={type === "expense" ? "default" : "ghost"}
              className="flex-1 h-12"
              onClick={() => setType("expense")}
              disabled={isSaving}
            >
              Expense
            </Button>
            <Button
              variant={type === "income" ? "default" : "ghost"}
              className="flex-1 h-12"
              onClick={() => setType("income")}
              disabled={isSaving}
            >
              Income
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount" className="text-base">Amount</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">
                $
              </span>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-10 text-2xl font-bold h-16 text-center"
                disabled={isSaving}
                autoFocus
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-base">Description</Label>
            <Input
              id="description"
              placeholder="What is this for?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-12 text-base"
              disabled={isSaving}
            />
          </div>

          <div className="space-y-4">
            <Label className="text-base">Category</Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {currentCategories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  disabled={isSaving}
                  className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-xl transition-all duration-200 ${
                    selectedCategory === category.name
                      ? "ring-2 ring-primary bg-primary/5 scale-105 shadow-sm"
                      : "hover:bg-muted hover:shadow-sm active:scale-95"
                  } ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-xl ${category.color} flex items-center justify-center text-xl sm:text-2xl shadow-sm`}>
                    {category.icon}
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-center">{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-base">Frequency</Label>
            <div className="flex gap-2 p-1 bg-muted rounded-lg">
              <Button
                variant={frequency === "weekly" ? "default" : "ghost"}
                className="flex-1 h-10"
                onClick={() => setFrequency("weekly")}
                disabled={isSaving}
              >
                Weekly
              </Button>
              <Button
                variant={frequency === "monthly" ? "default" : "ghost"}
                className="flex-1 h-10"
                onClick={() => setFrequency("monthly")}
                disabled={isSaving}
              >
                Monthly
              </Button>
            </div>

            {frequency === "weekly" && (
              <div className="space-y-2">
                <Label htmlFor="day-of-week" className="text-sm">Day of Week</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek} disabled={isSaving}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a day" />
                  </SelectTrigger>
                  <SelectContent>
                    {weekDays.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {frequency === "monthly" && (
              <div className="space-y-2">
                <Label htmlFor="day-of-month" className="text-sm">Day of Month</Label>
                <Select value={dayOfMonth} onValueChange={setDayOfMonth} disabled={isSaving}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a day" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
